import { ReactNode, use, useRef } from "react";
import clsx from "clsx";

import { FootnotesProvider } from "lib/footnotes/use-footnotes";
import { useContentInteraction } from "lib/interaction/use-content-interaction";
import { useKeyboardNavigation } from "lib/navigation/use-keyboard";
import { useMouseWheel } from "lib/navigation/use-mouse-wheel";
import { SettingsContext } from "lib/settings/use-settings";

import { Footer } from "./footer";
import { FragmentFinish } from "./fragment-finish";
import { Header } from "./header";
import css from "./layout.module.scss";
import { ReadInAppQR } from "./read-in-app";
import { Saleblock } from "./saleblock";
import { Stepper } from "./stepper";
import { Upsell } from "./upsell";

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { layoutDirection } = use(SettingsContext)!;
  const { handleWheel } = useMouseWheel();
  const rootRef = useRef<HTMLDivElement>(null);
  useKeyboardNavigation(rootRef);

  const {
    ref,
    swipeBindings,
    isChromeVisible,
    handleContentClick,
    handleMouseDown,
  } = useContentInteraction();

  return (
    <FootnotesProvider>
      <div ref={rootRef} tabIndex={-1} className={css.root}>
        <div className={clsx(css.header, !isChromeVisible && css.hidden)}>
          <Header />
        </div>
        <main className={css.main}>
          <div
            ref={ref}
            {...swipeBindings()}
            className={css.content}
            onMouseDown={handleMouseDown}
            onClick={handleContentClick}
            onWheel={handleWheel}
          >
            {children}
          </div>
          {layoutDirection === "horizontal" && (
            <div className={css.stepper}>
              <Stepper />
            </div>
          )}
          <div className={clsx(css.footer, !isChromeVisible && css.hidden)}>
            <Footer />
          </div>
        </main>
        <Upsell />
        <ReadInAppQR />
        <Saleblock />
        <FragmentFinish />
      </div>
    </FootnotesProvider>
  );
}
